import { Fragment, useMemo, useRef, useState, type ReactNode } from 'react';
import { Button, ColumnManager, Skeleton, Table, TableWrap, TBody, TD, TH, THead, TR, type FrozenSide } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { TextLink } from '@/components/ui/TextLink';
import { ApiError } from '@/lib/api';
import { cx } from '@/lib/cx';
import { useColumnLayout, useFrozenOffsets } from '@/lib/use-column-layout';
import {
  PiCardField,
  PiCardList,
  PiEmpty,
  PiError,
  PiExpandButton,
  PiLoading,
  PiSection,
  PiSortHeader,
  piCount,
} from '../pi-kit';
import {
  METRIC_LABEL,
  formatMonth,
  monthsCoveredText,
  monthsSpanText,
  type IcrViewMetrics,
  type IcrViewMonth,
  type IcrViewOverall,
  type IcrViewRow,
} from './icr-model';
import type { IcrMonthsState } from './use-icr-data';

/**
 * One row per university + rep country. Search narrows client-side (name,
 * school, rep country); sort is client-side, default Leads generated
 * descending, a missing figure sorts last. The totals row is the server's
 * `overall` while nothing is searched, the sum of the visible rows otherwise
 * (all filtered rows, not only the ones shown before "Show more"). A row
 * expands inline to its month-by-month figures, fetched by the page only once
 * the row is opened. University is pinned left; every other column is
 * managed under the `product-insights.icr.universities` layout scope.
 */

type SortColumn = 'university' | 'monthsCovered' | 'reports' | TableMetric;
type SortDir = 'asc' | 'desc';

const TABLE_METRICS = [
  'leadsGenerated',
  'leadsEngaged',
  'interestedNextIntake',
  'applicationsSubmitted',
  'admitted',
  'offersAccepted',
  'enrolled',
] as const satisfies readonly (keyof IcrViewMetrics)[];
type TableMetric = (typeof TABLE_METRICS)[number];

const SHORT_LABEL: Record<TableMetric, string> = {
  leadsGenerated: 'Leads',
  leadsEngaged: 'Engaged',
  interestedNextIntake: 'Interested',
  applicationsSubmitted: 'Applied',
  admitted: 'Admitted',
  offersAccepted: 'Accepted',
  enrolled: 'Enrolled',
};

const LAYOUT_SCOPE = 'product-insights.icr.universities';
const COLUMN_KEYS: readonly SortColumn[] = ['university', 'monthsCovered', 'reports', ...TABLE_METRICS];
const PINNED_LEFT = ['university'] as const;
const LOCKED_KEYS = ['university'] as const;
const NO_KEYS: readonly string[] = [];
const LAYOUT_OPTIONS = { pinnedLeftKeys: PINNED_LEFT };
const PAGE_SIZE = 50;

const COLUMN_LABEL: Record<SortColumn, string> = {
  university: 'University',
  monthsCovered: 'Months covered',
  reports: 'Reports',
  ...SHORT_LABEL,
};

const isMetric = (key: string): key is TableMetric => (TABLE_METRICS as readonly string[]).includes(key);
const isSortColumn = (key: string): key is SortColumn => (COLUMN_KEYS as readonly string[]).includes(key);

export interface IcrUniversityTableProps {
  rows: IcrViewRow[] | null;
  overall: IcrViewOverall | null;
  search: string;
  loading: boolean;
  error: unknown | null;
  expanded: ReadonlySet<string>;
  onToggle(key: string): void;
  months: Map<string, IcrMonthsState>;
  /** Optional: offered as the "Clear search" action when nothing matches. */
  onClearSearch?: () => void;
  /** Optional: offered as Retry on a load or refresh failure. */
  onRetry?: () => void;
}

function errorMessage(error: unknown): string {
  return error instanceof ApiError && error.message ? error.message : 'In-Country Representation could not be reached.';
}

function sortValue(row: IcrViewRow, column: Exclude<SortColumn, 'university'>): number | null {
  if (column === 'monthsCovered') return row.monthsCovered.count;
  if (column === 'reports') return row.reports;
  return row[column];
}

function sumOf(rows: IcrViewRow[], pick: (row: IcrViewRow) => number | null): number | null {
  let total = 0;
  let any = false;
  for (const row of rows) {
    const value = pick(row);
    if (value === null) continue;
    total += value;
    any = true;
  }
  return any ? total : null;
}

/** An unfrozen cell carries neither prop (TD decides positioning from `frozen` alone). */
function frozenPropsFor(
  key: string,
  frozenSideOf: (key: string) => FrozenSide | undefined,
  offsets: Record<string, number>,
): { frozen?: FrozenSide; frozenOffset?: number } {
  const side = frozenSideOf(key);
  if (side === undefined) return {};
  return { frozen: side, frozenOffset: offsets[key] ?? 0 };
}

export function IcrUniversityTable({
  rows,
  overall,
  search,
  loading,
  error,
  expanded,
  onToggle,
  months,
  onClearSearch,
  onRetry,
}: IcrUniversityTableProps) {
  const [sort, setSort] = useState<{ column: SortColumn; dir: SortDir }>({
    column: 'leadsGenerated',
    dir: 'desc',
  });

  const needle = search.trim().toLowerCase();

  // "Show more" resets whenever the search changes.
  const [shown, setShown] = useState<{ needle: string; count: number }>({ needle, count: PAGE_SIZE });
  const limit = shown.needle === needle ? shown.count : PAGE_SIZE;

  const layout = useColumnLayout(LAYOUT_SCOPE, COLUMN_KEYS, LOCKED_KEYS, NO_KEYS, LAYOUT_OPTIONS);
  const { visibleKeys, frozenSideOf } = layout;
  const headRowRef = useRef<HTMLTableRowElement>(null);
  const frozenOffsets = useFrozenOffsets(headRowRef, visibleKeys, frozenSideOf);
  const columns = useMemo(() => visibleKeys.filter(isSortColumn), [visibleKeys]);
  const cardColumns = useMemo(() => columns.filter((key) => key !== 'university'), [columns]);

  const visible = useMemo(() => {
    if (rows === null) return [];
    const filtered =
      needle === ''
        ? rows
        : rows.filter((row) =>
            [row.universityName, row.repCountry ?? '', row.company?.name ?? '', ...row.schools.map((s) => s.schoolName)]
              .join(' ')
              .toLowerCase()
              .includes(needle),
          );
    const factor = sort.dir === 'asc' ? 1 : -1;
    const byName = (a: IcrViewRow, b: IcrViewRow) =>
      a.universityName.localeCompare(b.universityName) || (a.repCountry ?? '').localeCompare(b.repCountry ?? '');
    return [...filtered].sort((a, b) => {
      if (sort.column === 'university') return byName(a, b) * factor;
      const left = sortValue(a, sort.column);
      const right = sortValue(b, sort.column);
      if (left === null && right === null) return byName(a, b);
      if (left === null) return 1;
      if (right === null) return -1;
      return left !== right ? (left - right) * factor : byName(a, b);
    });
  }, [rows, needle, sort]);

  const totals = useMemo(() => {
    if (needle === '' && overall !== null) {
      const fromOverall: Record<TableMetric | 'reports', number | null> = {
        reports: overall.reports,
        leadsGenerated: overall.leadsGenerated,
        leadsEngaged: overall.leadsEngaged,
        interestedNextIntake: overall.interestedNextIntake,
        applicationsSubmitted: overall.applicationsSubmitted,
        admitted: overall.admitted,
        offersAccepted: overall.offersAccepted,
        enrolled: overall.enrolled,
      };
      return fromOverall;
    }
    const summed = { reports: sumOf(visible, (r) => r.reports) } as Record<TableMetric | 'reports', number | null>;
    for (const metric of TABLE_METRICS) summed[metric] = sumOf(visible, (r) => r[metric]);
    return summed;
  }, [needle, overall, visible]);

  const onHeader = (column: SortColumn) =>
    setSort((current) =>
      current.column === column
        ? { column, dir: current.dir === 'asc' ? 'desc' : 'asc' }
        : { column, dir: column === 'university' ? 'asc' : 'desc' },
    );

  const ariaSort = (column: SortColumn) =>
    sort.column !== column ? undefined : sort.dir === 'asc' ? ('ascending' as const) : ('descending' as const);

  const expandButton = (row: IcrViewRow) => (
    <PiExpandButton
      expanded={expanded.has(row.key)}
      onClick={() => onToggle(row.key)}
      label={`${expanded.has(row.key) ? 'Hide' : 'Show'} months for ${row.universityName}`}
    />
  );

  const totalLabel = needle === '' ? 'Total' : 'Total of the rows shown';
  const sectionCount = rows === null ? null : visible.length;
  const columnManager =
    rows !== null && rows.length > 0 && !layout.loading ? (
      <ColumnManager layout={layout} labelOf={(key) => (isSortColumn(key) ? COLUMN_LABEL[key] : key)} lockedKeys={LOCKED_KEYS} />
    ) : undefined;

  let body: ReactNode;
  if (error !== null && rows === null) {
    body = (
      <div className="p-4">
        <PiError message={errorMessage(error)} onRetry={onRetry} />
      </div>
    );
  } else if (rows === null) {
    body = <PiLoading variant="table" rows={8} />;
  } else if (rows.length === 0) {
    body = <PiEmpty title="No In-Country Representation reports for this cycle" description="Try another cycle or report status." />;
  } else if (visible.length === 0) {
    body = (
      <PiEmpty
        title="No university matches this search"
        description="Clear the search to see every university."
        action={
          onClearSearch ? (
            <Button variant="secondary" onClick={onClearSearch}>
              Clear search
            </Button>
          ) : undefined
        }
      />
    );
  } else {
    const page = visible.slice(0, limit);
    const remaining = visible.length - page.length;

    const metricCell = (row: IcrViewRow | null, key: SortColumn) => {
      if (key === 'monthsCovered') return row === null ? null : <MonthsCoveredCell row={row} />;
      if (key === 'reports') return piCount(row === null ? totals.reports : row.reports);
      if (isMetric(key)) return piCount(row === null ? totals[key] : row[key]);
      return null;
    };

    body = (
      <div className={cx(loading && 'opacity-70')} aria-busy={loading}>
        {error !== null && (
          <div className="border-b border-border p-3">
            <PiError message={errorMessage(error)} onRetry={onRetry} />
          </div>
        )}

        {/* Desktop */}
        <div className="max-md:hidden">
          <TableWrap frozenHeader>
            <Table>
              <THead>
                <TR ref={headRowRef}>
                  {columns.map((key) =>
                    key === 'university' ? (
                      <TH
                        key={key}
                        aria-sort={ariaSort(key)}
                        className="min-w-72"
                        {...frozenPropsFor(key, frozenSideOf, frozenOffsets)}
                      >
                        <span className="pl-9">
                          <PiSortHeader
                            label={COLUMN_LABEL[key]}
                            active={sort.column === key}
                            direction={sort.dir}
                            onClick={() => onHeader(key)}
                          />
                        </span>
                      </TH>
                    ) : (
                      <TH
                        key={key}
                        numeric
                        aria-sort={ariaSort(key)}
                        title={isMetric(key) ? METRIC_LABEL[key] : undefined}
                        className={key === 'monthsCovered' ? 'min-w-28' : 'min-w-24'}
                        {...frozenPropsFor(key, frozenSideOf, frozenOffsets)}
                      >
                        <PiSortHeader
                          label={COLUMN_LABEL[key]}
                          active={sort.column === key}
                          direction={sort.dir}
                          onClick={() => onHeader(key)}
                          align="right"
                        />
                      </TH>
                    ),
                  )}
                </TR>
              </THead>
              <TBody>
                {page.map((row) => {
                  const open = expanded.has(row.key);
                  return (
                    <Fragment key={row.key}>
                      <TR>
                        {columns.map((key) =>
                          key === 'university' ? (
                            <TD key={key} className="max-w-96" {...frozenPropsFor(key, frozenSideOf, frozenOffsets)}>
                              <div className="flex min-w-0 items-center gap-1">
                                <div className="shrink-0">{expandButton(row)}</div>
                                <UniversityCell row={row} />
                              </div>
                            </TD>
                          ) : (
                            <TD
                              key={key}
                              numeric
                              className={cx('tabular-nums', key === 'leadsGenerated' && 'font-medium')}
                              {...frozenPropsFor(key, frozenSideOf, frozenOffsets)}
                            >
                              {metricCell(row, key)}
                            </TD>
                          ),
                        )}
                      </TR>
                      {open && (
                        <TR>
                          <TD colSpan={Math.max(columns.length, 1)} className="bg-surface-sunken">
                            <div className="pl-9">
                              <MonthsPanel state={months.get(row.key) ?? { kind: 'loading' }} />
                            </div>
                          </TD>
                        </TR>
                      )}
                    </Fragment>
                  );
                })}
                <TR className="border-t border-hairline-strong">
                  {columns.map((key) =>
                    key === 'university' ? (
                      <TD key={key} className="font-medium" {...frozenPropsFor(key, frozenSideOf, frozenOffsets)}>
                        <span className="pl-9">{totalLabel}</span>
                      </TD>
                    ) : (
                      <TD
                        key={key}
                        numeric
                        className="font-medium tabular-nums"
                        {...frozenPropsFor(key, frozenSideOf, frozenOffsets)}
                      >
                        {metricCell(null, key)}
                      </TD>
                    ),
                  )}
                </TR>
              </TBody>
            </Table>
          </TableWrap>
        </div>

        {/* Phone */}
        <div className="p-3 md:hidden">
          <PiCardList
            items={page}
            getKey={(row) => row.key}
            render={(row) => (
              <div>
                <div className="flex items-start justify-between gap-2">
                  <UniversityCell row={row} />
                  <div className="shrink-0">{expandButton(row)}</div>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
                  {cardColumns.map((key) => (
                    <PiCardField
                      key={key}
                      label={isMetric(key) ? METRIC_LABEL[key] : COLUMN_LABEL[key]}
                      value={
                        key === 'monthsCovered'
                          ? (monthsCoveredText(row.monthsCovered) ?? '—')
                          : metricCell(row, key)
                      }
                    />
                  ))}
                </div>
                {expanded.has(row.key) && (
                  <div className="mt-3 border-t border-border pt-3">
                    <MonthsPanel state={months.get(row.key) ?? { kind: 'loading' }} />
                  </div>
                )}
              </div>
            )}
          />
          <div className="mt-2 rounded-md border border-hairline-strong bg-surface-sunken p-3">
            <div className="font-medium">{totalLabel}</div>
            <div className="mt-2 grid grid-cols-2 gap-x-3 gap-y-1">
              {cardColumns
                .filter((key) => key !== 'monthsCovered')
                .map((key) => (
                  <PiCardField
                    key={key}
                    label={isMetric(key) ? METRIC_LABEL[key] : COLUMN_LABEL[key]}
                    value={metricCell(null, key)}
                  />
                ))}
            </div>
          </div>
        </div>

        {remaining > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-2 border-t border-border px-4 py-2 text-sm text-fg-muted">
            <span className="tabular-nums">
              {piCount(page.length)} of {piCount(visible.length)}
            </span>
            <Button
              variant="secondary"
              onClick={() => setShown({ needle, count: limit + PAGE_SIZE })}
            >
              Show {piCount(Math.min(PAGE_SIZE, remaining))} more
            </Button>
          </div>
        )}
      </div>
    );
  }

  return (
    <PiSection title="Universities" count={sectionCount} actions={columnManager} flush>
      {body}
    </PiSection>
  );
}

function UniversityCell({ row }: { row: IcrViewRow }) {
  const schools = row.schools.map((s) => s.schoolName).join(', ');
  const rep = row.repCountry ?? row.repCountryCode;
  return (
    <div className="min-w-0">
      <div className="truncate font-medium text-fg">
        {row.company !== null ? (
          <TextLink to={`/companies/${row.company.id}`}>{row.universityName}</TextLink>
        ) : (
          row.universityName
        )}
      </div>
      <div className="flex min-w-0 flex-wrap items-center gap-x-2 text-xs text-fg-muted">
        {rep !== null && <span className="whitespace-nowrap">Rep in {rep}</span>}
        {row.representatives !== null && row.representatives > 1 && (
          <span className="whitespace-nowrap">{piCount(row.representatives)} representatives</span>
        )}
        {schools !== '' && (
          <span className="truncate" title={schools}>
            {schools}
          </span>
        )}
      </div>
    </div>
  );
}

function MonthsCoveredCell({ row }: { row: IcrViewRow }) {
  const text = monthsCoveredText(row.monthsCovered);
  const span = monthsSpanText(row.monthsCovered);
  return <span title={span ?? undefined}>{text ?? '—'}</span>;
}

function MonthsPanel({ state }: { state: IcrMonthsState }) {
  if (state.kind === 'loading') {
    return (
      <div className="space-y-1.5 py-1" aria-busy="true">
        <Skeleton className="h-6 w-full max-w-xl" />
        <Skeleton className="h-6 w-full max-w-xl" />
      </div>
    );
  }
  if (state.kind === 'error') {
    return (
      <Notice tone="danger" title="Month-by-month figures could not be loaded">
        {errorMessage(state.error)}
      </Notice>
    );
  }
  if (state.months === null || state.months.length === 0) {
    return <p className="py-2 text-sm text-fg-muted">No month-by-month figures for this university.</p>;
  }
  return <MonthsTable months={state.months} />;
}

function MonthsTable({ months }: { months: IcrViewMonth[] }) {
  return (
    <TableWrap>
      <Table className="min-w-[640px]">
        <THead>
          <TR>
            <TH>Month</TH>
            <TH numeric>Reports</TH>
            {TABLE_METRICS.map((metric) => (
              <TH key={metric} numeric title={METRIC_LABEL[metric]}>
                {SHORT_LABEL[metric]}
              </TH>
            ))}
          </TR>
        </THead>
        <TBody>
          {months.map((month) => (
            <TR key={month.month}>
              <TD className="whitespace-nowrap">{formatMonth(month.month)}</TD>
              <TD numeric className="tabular-nums">
                {piCount(month.reports)}
              </TD>
              {TABLE_METRICS.map((metric) => (
                <TD key={metric} numeric className="tabular-nums">
                  {piCount(month[metric])}
                </TD>
              ))}
            </TR>
          ))}
        </TBody>
      </Table>
    </TableWrap>
  );
}
